/**
 * Enhanced error handling for registration and payment flows
 */

export interface RegistrationError {
  code: string;
  message: string;
  userMessage: string;
  recoverable: boolean;
  action?: 'retry' | 'contact_support' | 'check_email' | 'check_payment';
}

/**
 * Parse Stripe checkout errors into user-friendly messages
 */
export function parseStripeError(error: any): RegistrationError {
  const errorMessage = error?.message || '';
  const errorCode = error?.code || 'unknown';

  // Card declined errors
  if (errorMessage.includes('card_declined') || errorCode === 'card_declined') {
    return {
      code: 'card_declined',
      message: errorMessage,
      userMessage: 'Your card was declined. Please check your card details or try a different payment method.',
      recoverable: true,
      action: 'retry'
    };
  }

  // Insufficient funds
  if (errorMessage.includes('insufficient_funds')) {
    return {
      code: 'insufficient_funds',
      message: errorMessage,
      userMessage: 'Your card has insufficient funds. Please use a different payment method.',
      recoverable: true,
      action: 'retry'
    };
  }

  // Expired card
  if (errorMessage.includes('expired_card')) {
    return {
      code: 'expired_card',
      message: errorMessage,
      userMessage: 'Your card has expired. Please use a different payment method.',
      recoverable: true,
      action: 'retry'
    };
  }

  // Invalid card details
  if (errorMessage.includes('incorrect_') || errorMessage.includes('invalid_')) {
    return {
      code: 'invalid_card',
      message: errorMessage,
      userMessage: 'The card details provided are invalid. Please check and try again.',
      recoverable: true,
      action: 'retry'
    };
  }

  // Payment processing error
  if (errorMessage.includes('processing_error')) {
    return {
      code: 'processing_error',
      message: errorMessage,
      userMessage: 'There was an error processing your payment. Please try again in a few moments.',
      recoverable: true,
      action: 'retry'
    };
  }

  // Session expired
  if (errorMessage.includes('expired') || errorMessage.includes('session')) {
    return {
      code: 'session_expired',
      message: errorMessage,
      userMessage: 'Your payment session has expired. Please start the registration process again.',
      recoverable: true,
      action: 'retry'
    };
  }

  // Network errors
  if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
    return {
      code: 'network_error',
      message: errorMessage,
      userMessage: 'Network connection issue. Please check your internet connection and try again.',
      recoverable: true,
      action: 'retry'
    };
  }

  // Default error
  return {
    code: 'unknown_error',
    message: errorMessage || 'Unknown error occurred',
    userMessage: 'An unexpected error occurred during payment. Please contact support if this persists.',
    recoverable: false,
    action: 'contact_support'
  };
}

/**
 * Parse company access request errors
 */
export function parseCompanyAccessError(error: any): RegistrationError {
  const errorMessage = error?.message || '';

  if (errorMessage.includes('already exists') || errorMessage.includes('duplicate')) {
    return {
      code: 'duplicate_request',
      message: errorMessage,
      userMessage: 'You have already submitted an access request to this company. Please wait for approval or contact your company administrator.',
      recoverable: false,
      action: 'check_email'
    };
  }

  if (errorMessage.includes('not found') || errorMessage.includes('company')) {
    return {
      code: 'company_not_found',
      message: errorMessage,
      userMessage: 'The company could not be found. Please verify the company details and try again.',
      recoverable: true,
      action: 'retry'
    };
  }

  if (errorMessage.includes('email') || errorMessage.includes('invalid')) {
    return {
      code: 'invalid_email',
      message: errorMessage,
      userMessage: 'The email address is invalid or already in use. Please use a different email address.',
      recoverable: true,
      action: 'retry'
    };
  }

  return {
    code: 'access_request_failed',
    message: errorMessage || 'Failed to submit access request',
    userMessage: 'Failed to submit your access request. Please try again or contact support.',
    recoverable: true,
    action: 'contact_support'
  };
}

/**
 * Parse FCA validation errors
 */
export function parseFCAValidationError(error: any): RegistrationError {
  const errorMessage = error?.message || '';

  if (errorMessage.includes('not found') || errorMessage.includes('invalid')) {
    return {
      code: 'fca_not_found',
      message: errorMessage,
      userMessage: 'The FCA number could not be verified. Please check the number and try again. If the issue persists, contact support.',
      recoverable: true,
      action: 'retry'
    };
  }

  if (errorMessage.includes('api') || errorMessage.includes('service')) {
    return {
      code: 'fca_service_error',
      message: errorMessage,
      userMessage: 'Unable to verify FCA number at this time. Please try again in a few moments.',
      recoverable: true,
      action: 'retry'
    };
  }

  return {
    code: 'fca_validation_failed',
    message: errorMessage || 'FCA validation failed',
    userMessage: 'Unable to validate FCA number. Please contact support for assistance.',
    recoverable: false,
    action: 'contact_support'
  };
}

/**
 * Get user-friendly error message and recovery action
 */
export function getErrorDetails(error: any, context: 'payment' | 'access_request' | 'fca_validation'): RegistrationError {
  switch (context) {
    case 'payment':
      return parseStripeError(error);
    case 'access_request':
      return parseCompanyAccessError(error);
    case 'fca_validation':
      return parseFCAValidationError(error);
    default:
      return {
        code: 'unknown',
        message: error?.message || 'Unknown error',
        userMessage: 'An unexpected error occurred. Please try again or contact support.',
        recoverable: false,
        action: 'contact_support'
      };
  }
}
